import PropTypes from "prop-types";

import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";
import Icon from "@mui/material/Icon";
import { useTheme } from "@mui/material/styles"; 
import { alpha } from "@mui/material/styles"; 

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

function ComplexStatisticsCard({ color, title, count, percentage, icon }) {
  const theme = useTheme(); 

  
  
  const getIconBoxBackground = (colorName) => {
    const mainColor = theme.palette[colorName]?.main || theme.palette.info.main; 
    const lighterColor = theme.palette[colorName]?.light || theme.palette.info.light;
    return `linear-gradient(195deg, ${lighterColor} 0%, ${mainColor} 100%)`;
  };

  return (
    <Card
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        height: "100%",
        p: 3, 
        borderRadius: theme.borders.borderRadius.xl,
        boxShadow: theme.shadows[4], 
        transition: "transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out", 
        "&:hover": {
          transform: "translateY(-8px)", 
          boxShadow: theme.shadows[8], 
        },
        overflow: "visible", 
      }}
    >
      <MDBox
        display="flex"
        justifyContent="space-between"
        alignItems="flex-start" 
        mb={2} 
      >
        <MDBox
          variant="gradient"
          bgColor={color} 
          color={color === "light" ? "dark" : "white"}
          coloredShadow={color} 
          borderRadius="lg"
          display="flex"
          justifyContent="center"
          alignItems="center"
          width="4rem" 
          height="4rem" 
          mt={-5} 
          sx={{
            flexShrink: 0,
            background: getIconBoxBackground(color), 
            boxShadow: theme.shadows.lg, 
            position: "relative", 
            zIndex: 1, 
            
            animation: `pulseIn 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94) both`,
            "@keyframes pulseIn": {
              "0%": { opacity: 0, transform: "scale(0.8) translateY(10px)" },
              "100%": { opacity: 1, transform: "scale(1) translateY(0)" },
            },
          }}
        >
          <Icon fontSize="large" color="inherit">
            {icon}
          </Icon>
        </MDBox>
        <MDBox textAlign="right" lineHeight={1.3}>
          <MDTypography variant="overline" color="text.secondary" fontWeight="medium" sx={{ display: 'block', mb: 0.5 }}>
            {}
            {title}
          </MDTypography>
          <MDTypography variant="h4" fontWeight="bold" color="text.primary">
            {count}
          </MDTypography>
        </MDBox>
      </MDBox>

      {percentage && percentage.text && (
        <>
          <Divider sx={{ my: 1.5 }} /> {}
          <MDBox display="flex" alignItems="center" pt={0.5} pb={0}>
            <MDTypography
              variant="subtitle2" 
              color={percentage.color}
              fontWeight="bold"
              sx={{ mr: 0.5 }} 
            >
              {percentage.amount}
            </MDTypography>
            <MDTypography variant="caption" color="text.secondary" lineHeight={1.4}>
              {percentage.label}
            </MDTypography>
          </MDBox>
        </>
      )}
    </Card>
  );
}

ComplexStatisticsCard.defaultProps = {
  color: "info",
  percentage: {
    color: "success",
    text: "",
    label: "",
  },
};

ComplexStatisticsCard.propTypes = {
  color: PropTypes.oneOf([
    "primary",
    "secondary",
    "info",
    "success",
    "warning",
    "error",
    "light",
    "dark",
  ]),
  title: PropTypes.string.isRequired,
  count: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  percentage: PropTypes.shape({
    color: PropTypes.oneOf([
      "primary",
      "secondary",
      "info",
      "success",
      "warning",
      "error",
      "dark",
      "white",
    ]),
    amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    label: PropTypes.string,
  }),
  icon: PropTypes.node.isRequired,
};

export default ComplexStatisticsCard;